import React, { useState } from 'react';
import { Upload, CheckCircle, XCircle, Loader, Package } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProductImport = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [importType, setImportType] = useState('google'); // 'simple', 'ebay', or 'google'

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.name.endsWith('.csv')) {
      setFile(selectedFile);
      setResult(null);
    } else {
      toast.error('Please select a CSV file');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    // Quick CSV validation before upload
    let fileContent;
    try {
      fileContent = await file.text();
      const lines = fileContent.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        toast.error(language === 'it' 
          ? 'File CSV vuoto o invalido (deve avere almeno header + 1 riga dati)'
          : 'Empty or invalid CSV file (must have at least header + 1 data row)');
        return;
      }
      
    } catch (err) {
      toast.error(language === 'it' ? 'Impossibile leggere il file' : 'Cannot read file');
      return;
    }

    setUploading(true);
    setResult(null);
    setError('');
    
    const formData = new FormData();
    // Create a new Blob from the text content to avoid "body stream already read" error
    const fileBlob = new Blob([fileContent], { type: 'text/csv' });
    formData.append('file', fileBlob, file.name);

    let data = null;
    
    try {
      
      // Show loading toast for large files
      const fileSize = file.size / (1024 * 1024); // MB
      if (fileSize > 1) {
        toast.info(language === 'it' 
          ? `Caricamento ${file.name} (${fileSize.toFixed(1)} MB)... Attendere...` 
          : `Uploading ${file.name} (${fileSize.toFixed(1)} MB)... Please wait...`,
          { duration: 10000 }
        );
      }
      
      // Choose endpoint based on import type
      let endpoint;
      if (importType === 'export') {
        endpoint = `${BACKEND_URL}/api/import-export-csv`;
      } else if (importType === 'google') {
        endpoint = `${BACKEND_URL}/api/import-google-feed`;
      } else if (importType === 'simple') {
        endpoint = `${BACKEND_URL}/api/import-simple-csv`;
      } else {
        endpoint = `${BACKEND_URL}/api/import-csv`;
      }
      
      
      // Create abort controller for timeout (5 minutes for large files)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 min timeout
        
      const response = await fetch(endpoint, {
        method: 'POST',
        body: formData,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      // READ BODY IMMEDIATELY - BEFORE any other operation!
      let bodyText = '';
      try {
        bodyText = await response.text();
      } catch (readErr) {
        throw new Error(language === 'it' 
          ? 'Impossibile leggere la risposta del server' 
          : 'Cannot read server response');
      }

      // NOW we can safely access status, headers, etc.
      const contentType = response.headers.get('content-type') || '';
      
      // Parse body as JSON if possible
      let data = null;
      
      if (contentType.includes('application/json') && bodyText) {
        try {
          data = JSON.parse(bodyText);
        } catch (jsonErr) {
          throw new Error(language === 'it' 
            ? 'Risposta JSON invalida dal server' 
            : 'Invalid JSON response from server');
        }
      } else {
        // Not JSON
        data = { error: bodyText || 'Empty response' };
      }
      
      // Check if response was successful
      if (!response.ok) {
        const errorMsg = data?.detail || data?.message || data?.error || bodyText.substring(0, 200) || `Server error ${response.status}`;
        throw new Error(errorMsg);
      }
      
      // Success!

      setResult(data);
      const message = language === 'it' 
        ? `Importati ${data.imported || 0} prodotti, aggiornati ${data.updated || 0}!`
        : `Successfully imported ${data.imported || 0} products, updated ${data.updated || 0}!`;
      toast.success(message);
      
    } catch (error) {
      
      let errorMsg = 'Failed to upload file';
      
      if (error.name === 'AbortError') {
        errorMsg = language === 'it'
          ? 'Timeout: il file è troppo grande o l\'operazione sta richiedendo troppo tempo. Prova con un file più piccolo.'
          : 'Timeout: file is too large or operation taking too long. Try with a smaller file.';
      } else {
        errorMsg = error.message || 'Failed to upload file';
      }
      
      setError(errorMsg);
      toast.error(`Error: ${errorMsg}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="dark-container pt-20 min-h-screen">
      <div className="max-w-4xl mx-auto px-6 py-20">
        <div className="flex items-center justify-between mb-8">
          <h1 className="display-large">
            {language === 'it' ? 'Importa Prodotti' : 'Import Products'}
          </h1>
          
          <button 
            onClick={() => navigate('/admin/inventory')}
            className="btn-secondary flex items-center gap-2"
          >
            <Package size={20} />
            {language === 'it' ? 'Gestione Inventario' : 'Inventory Management'}
          </button>
          <button 
            onClick={() => navigate('/admin/orders')}
            className="btn-secondary text-sm py-2 px-4"
          >
            📦 {language === 'it' ? 'Ordini' : 'Orders'}
          </button>
          <button 
            onClick={() => navigate('/admin/newsletter')}
            className="btn-secondary text-sm py-2 px-4"
          >
            📧 {language === 'it' ? 'Newsletter' : 'Newsletter'}
          </button>
        </div>

        {/* Import Type Selector */}
        <div className="dark-card p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">
            {language === 'it' ? 'Tipo di CSV' : 'CSV Type'}
          </h3>
          <div className="flex flex-col gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="importType"
                value="google"
                checked={importType === 'google'}
                onChange={(e) => setImportType(e.target.value)}
                className="w-4 h-4"
              />
              <span>
                <strong>{language === 'it' ? '🛍️ Feed Google Shopping' : '🛍️ Google Shopping Feed'}</strong>
                <span className="text-sm text-white/60 ml-2 block">
                  (id, title, description, price, availability, image_link, brand...)
                </span>
              </span>
            </label>
            
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="importType"
                value="simple"
                checked={importType === 'simple'}
                onChange={(e) => setImportType(e.target.value)}
                className="w-4 h-4"
              />
              <span>
                {language === 'it' ? 'Formato Semplice' : 'Simple Format'}
                <span className="text-sm text-white/60 ml-2 block">
                  (codice, titolo, marca, prezzo, quantita...)
                </span>
              </span>
            </label>
            
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="importType"
                value="ebay"
                checked={importType === 'ebay'}
                onChange={(e) => setImportType(e.target.value)}
                className="w-4 h-4"
              />
              <span>
                {language === 'it' ? 'Formato eBay' : 'eBay Format'}
              </span>
            </label>
            
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="importType"
                value="export"
                checked={importType === 'export'}
                onChange={(e) => setImportType(e.target.value)}
                className="w-4 h-4"
              />
              <span className="text-[var(--brand-primary)] font-semibold">
                {language === 'it' ? 'Export ALLMX (backup/migrazione)' : 'ALLMX Export (backup/migration)'}
              </span>
            </label>
          </div>
        </div>

        {/* Upload Section */}
        <div className="dark-card p-8 mb-8">
          <div className="border-2 border-dashed border-white/25 rounded-lg p-12 text-center">
            <Upload size={48} className="mx-auto mb-4" style={{ color: 'var(--brand-primary)' }} />
            
            <h3 className="text-xl font-semibold mb-2">
              {language === 'it' ? 'Carica File CSV' : 'Upload CSV File'}
            </h3>
            <p className="body-medium mb-6">
              {language === 'it' 
                ? 'Seleziona il file CSV con i prodotti da importare' 
                : 'Select the CSV file with products to import'}
            </p>

            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
              id="csv-upload"
            />
            
            <label htmlFor="csv-upload" className="btn-secondary cursor-pointer">
              {file ? file.name : (language === 'it' ? 'Seleziona File' : 'Select File')}
            </label>

            {file && (
              <div className="mt-6">
                {error && (
                  <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded text-red-500">
                    <strong>Errore:</strong> {error}
                  </div>
                )}
                
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="btn-primary"
                >
                  {uploading ? (
                    <>
                      <Loader size={20} className="animate-spin" />
                      {language === 'it' ? 'Importando...' : 'Importing...'}
                    </>
                  ) : (
                    <>
                      <Upload size={20} />
                      {language === 'it' ? 'Importa Prodotti' : 'Import Products'}
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Results Section */}
        {result && (
          <div className="dark-card p-8">
            <h2 className="display-medium mb-6">
              {language === 'it' ? 'Risultati Importazione' : 'Import Results'}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="flex items-center gap-4 p-4 bg-green-500/20 border border-green-500/50 rounded">
                <CheckCircle size={32} className="text-green-500" />
                <div>
                  <div className="text-2xl font-bold">{result.imported}</div>
                  <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
                    {language === 'it' ? 'Nuovi Prodotti' : 'New Products'}
                  </div>
                </div>
              </div>

              {result.updated > 0 && (
                <div className="flex items-center gap-4 p-4 bg-blue-500/20 border border-blue-500/50 rounded">
                  <CheckCircle size={32} className="text-blue-500" />
                  <div>
                    <div className="text-2xl font-bold">{result.updated}</div>
                    <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
                      {language === 'it' ? 'Prodotti Aggiornati' : 'Products Updated'}
                    </div>
                  </div>
                </div>
              )}

              {result.skipped > 0 && (
                <div className="flex items-center gap-4 p-4 bg-red-500/20 border border-red-500/50 rounded">
                  <XCircle size={32} className="text-red-500" />
                  <div>
                    <div className="text-2xl font-bold">{result.skipped}</div>
                    <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
                      {language === 'it' ? 'Prodotti Saltati' : 'Products Skipped'}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {result.errors && result.errors.length > 0 && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-3 text-red-400">
                  {language === 'it' ? 'Errori:' : 'Errors:'}
                </h3>
                <ul className="space-y-2">
                  {result.errors.map((error, index) => (
                    <li key={`err-${error.substring(0, 30)}-${index}`} className="text-sm text-red-300 bg-red-500/10 p-3">
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Instructions */}
        <div className="dark-card p-8 mt-8">
          <h3 className="text-xl font-semibold mb-4">
            {language === 'it' ? 'Istruzioni' : 'Instructions'}
          </h3>
          <ul className="space-y-3 body-medium">
            <li>
              {language === 'it'
                ? '• Il sistema importa automaticamente prodotti da file CSV formato eBay'
                : '• The system automatically imports products from eBay format CSV files'}
            </li>
            <li>
              {language === 'it'
                ? '• Le descrizioni vengono tradotte automaticamente in inglese'
                : '• Descriptions are automatically translated to English'}
            </li>
            <li>
              {language === 'it'
                ? '• I prodotti vengono categorizzati automaticamente usando AI'
                : '• Products are automatically categorized using AI'}
            </li>
            <li>
              {language === 'it'
                ? '• Se un prodotto esiste già (stesso SKU), verrà aggiornato'
                : '• If a product already exists (same SKU), it will be updated'}
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ProductImport;
